/**
 * MultiSelectCombobox Component
 * 
 * Autocomplete input que permite seleção múltipla com criação automática
 * Ideal para tags, stakeholders, etc.
 */

import * as React from "react"
import { Check, X, ChevronsUpDown } from "lucide-react"
import { cn } from "./utils"
import { Button } from "./button"
import { Badge } from "./badge"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"

export interface MultiSelectOption {
  value: string
  label: string
}

interface MultiSelectComboboxProps {
  options: MultiSelectOption[]
  value?: string // Valores separados por vírgula: "João, Maria"
  onValueChange?: (value: string) => void
  placeholder?: string
  emptyText?: string
  searchPlaceholder?: string
  className?: string
  disabled?: boolean
  onCreate?: (value: string) => void | Promise<void>
}

export function MultiSelectCombobox({
  options,
  value,
  onValueChange,
  placeholder = "Selecionar...",
  emptyText = "Nenhum resultado encontrado",
  searchPlaceholder = "Buscar ou adicionar...",
  className,
  disabled = false,
  onCreate,
}: MultiSelectComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  // Converter string com vírgulas em array
  const selectedValues = React.useMemo(() => {
    if (!value) return []
    return value.split(',').map(v => v.trim()).filter(Boolean)
  }, [value])

  // Adicionar um valor
  const addValue = (newValue: string) => {
    if (!newValue || selectedValues.includes(newValue)) return
    
    const updated = [...selectedValues, newValue].join(', ')
    onValueChange?.(updated)
  }

  // Remover um valor
  const removeValue = (valueToRemove: string) => {
    const updated = selectedValues.filter(v => v !== valueToRemove).join(', ')
    onValueChange?.(updated)
  }

  // Selecionar da lista
  const handleSelect = (currentValue: string) => {
    if (selectedValues.includes(currentValue)) {
      removeValue(currentValue)
    } else {
      addValue(currentValue)
    }
  }

  // Criar novo ao pressionar Enter
  const handleInputKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && search) {
      e.preventDefault()
      
      const trimmedSearch = search.trim()
      if (!trimmedSearch) return

      // Verificar se já existe na lista de opções
      const existingOption = options.find(
        o => o.value.toLowerCase() === trimmedSearch.toLowerCase()
      )

      if (existingOption) {
        // Se já existe, apenas seleciona
        handleSelect(existingOption.value)
      } else {
        // Se não existe, cria novo
        if (onCreate) {
          await onCreate(trimmedSearch)
        }
        addValue(trimmedSearch)
      }
      
      setSearch("")
    }
  }

  return (
    <div className="space-y-2">
      {/* Badges dos selecionados */}
      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedValues.map((val) => (
            <Badge
              key={val}
              variant="secondary"
              className="gap-1 pr-1"
            >
              <span>{val}</span>
              <button
                type="button"
                onClick={() => removeValue(val)}
                className="ml-1 rounded-full hover:bg-muted"
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Combobox */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn("w-full justify-between", className)}
            disabled={disabled}
          >
            <span className="truncate text-muted-foreground">
              {selectedValues.length > 0
                ? `${selectedValues.length} ${selectedValues.length === 1 ? 'selecionado' : 'selecionados'}`
                : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
              onKeyDown={handleInputKeyDown}
            />
            <CommandList>
              <CommandEmpty>
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  {search
                    ? `Pressione Enter para adicionar "${search}"`
                    : emptyText}
                </div>
              </CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selectedValues.includes(option.value)
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={handleSelect}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}









